import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { useApp } from "@/context/AppContext";
import { POLICIES, POLICY_ORDER } from "@/i18n/policies";

// Internal link markers inside policy text: [label|target]
// target "contact_link" -> /pages/contact, "track_link" -> /track, etc.
const LINK_TARGETS = {
  contact_link: "/pages/contact",
  cookies_link: "/pages/cookies",
  returns_link: "/pages/returns",
  track_link: "/track",
  orders_link: "/account/orders",
};

function renderInline(text, keyBase) {
  const parts = [];
  let rest = text;
  let i = 0;
  // bold **...**
  const rx = /\*\*(.+?)\*\*|\[([^|\]]+)\|([^\]]+)\]/g;
  let m;
  let last = 0;
  while ((m = rx.exec(rest)) !== null) {
    if (m.index > last) parts.push(<span key={`${keyBase}-t${i++}`}>{rest.slice(last, m.index)}</span>);
    if (m[1]) {
      parts.push(<strong key={`${keyBase}-b${i++}`}>{m[1]}</strong>);
    } else {
      const [label, target] = [m[2], m[3]];
      const to = LINK_TARGETS[target] || target;
      parts.push(<Link key={`${keyBase}-l${i++}`} to={to} className="text-brand underline underline-offset-2 hover:brightness-110">{label}</Link>);
    }
    last = m.index + m[0].length;
  }
  if (last < rest.length) parts.push(<span key={`${keyBase}-t${i++}`}>{rest.slice(last)}</span>);
  return parts;
}

export default function PolicyPage() {
  const { slug } = useParams();
  const { lang } = useI18n();
  const policy = POLICIES[slug];
  if (!policy) return <Navigate to="/" replace />;
  const c = policy[lang] || policy.en;

  return (
    <div className="mx-auto max-w-3xl px-4 py-10" data-testid="policy-page">
      <nav className="mb-6 text-xs text-muted-foreground">
        <Link to="/" className="hover:text-brand">KHA</Link>
        <span className="mx-1.5">/</span>
        <span className="text-ink">{c.title}</span>
      </nav>
      <h1 className="font-display text-3xl font-bold text-ink">{c.title}</h1>
      <div className="mt-6 space-y-4 text-sm leading-7 text-muted-foreground">
        {c.body.map((line, idx) => {
          const k = `${slug}-${idx}`;
          if (line.startsWith("- ")) {
            return (
              <li key={k} className="ms-5 list-disc">{renderInline(line.slice(2), k)}</li>
            );
          }
          return <p key={k}>{renderInline(line, k)}</p>;
        })}
      </div>

      <div className="mt-10 border-t border-border pt-6">
        <h2 className="mb-3 text-sm font-bold text-ink">{/* more policies */}
          {lang === "ar" ? "صفحات أخرى" : lang === "tr" ? "Diğer sayfalar" : "Other pages"}
        </h2>
        <div className="flex flex-wrap gap-2">
          {POLICY_ORDER.filter(([s]) => s !== slug).map(([s, nameKey]) => (
            <Link key={s} to={`/pages/${s}`} className="rounded-full border border-border px-4 py-1.5 text-xs font-medium text-muted-foreground hover:border-brand hover:text-brand">
              {nameKey === "about_us" ? (lang === "ar" ? "من نحن" : lang === "tr" ? "Hakkımızda" : "About Us")
               : nameKey === "contact_us" ? (lang === "ar" ? "تواصل معنا" : lang === "tr" ? "Bize Ulaşın" : "Contact Us")
               : nameKey === "shipping_policy" ? (lang === "ar" ? "الشحن والتوصيل" : lang === "tr" ? "Kargo ve Teslimat" : "Shipping & Delivery")
               : nameKey === "returns_policy" ? (lang === "ar" ? "الإرجاع والاستبدال" : lang === "tr" ? "İade ve Geri Ödeme" : "Returns & Refunds")
               : nameKey === "cancellation_policy" ? (lang === "ar" ? "الإلغاء" : lang === "tr" ? "İptal" : "Cancellation")
               : nameKey === "terms_conditions" ? (lang === "ar" ? "الشروط والأحكام" : lang === "tr" ? "Şartlar ve Koşullar" : "Terms & Conditions")
               : nameKey === "privacy_policy" ? (lang === "ar" ? "الخصوصية" : lang === "tr" ? "Gizlilik" : "Privacy")
               : nameKey === "cookie_policy" ? (lang === "ar" ? "الكوكيز" : lang === "tr" ? "Çerezler" : "Cookies")
               : nameKey === "faq" ? "FAQ"
               : (lang === "ar" ? "تتبع الطلب" : lang === "tr" ? "Sipariş Takibi" : "Order Tracking")}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
